/**
 * PortalSidebar — left navigation for the faculty/vendor portal.
 * Mirrors the app sidebar's quality but with the small, fixed portal nav set.
 */

import {
  CalendarClock,
  ClipboardCheck,
  FilePlus2,
  FileText,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  UserCircle,
  type LucideIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useLogoutMutation } from '@/hooks/useAuth';

export interface PortalNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

export const PORTAL_NAV: PortalNavItem[] = [
  { to: '/portal', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/portal/invoices/new', label: 'Submit Invoice', icon: FilePlus2 },
  { to: '/portal/invoices', label: 'My Invoices', icon: FileText, end: true },
  { to: '/portal/batches', label: 'My Batches', icon: GraduationCap },
  { to: '/portal/attendance', label: 'My Attendance', icon: CalendarClock },
  { to: '/portal/profile', label: 'My Profile', icon: UserCircle },
];

// Faculty who haven't completed registration (no profile/Party yet) get a
// reduced nav — the rest of the portal needs a profile to function.
const INCOMPLETE_NAV: PortalNavItem[] = [
  { to: '/portal', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/portal/complete-registration', label: 'Complete registration', icon: ClipboardCheck },
];

export default function PortalSidebar({ className }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  const logout = useLogoutMutation();
  const navItems = user != null && !user.party_id ? INCOMPLETE_NAV : PORTAL_NAV;

  return (
    <aside className={cn('w-60 shrink-0 flex-col border-r border-border bg-card', className)}>
      {/* Brand */}
      <div className="flex h-16 items-center gap-2 border-b border-border px-5">
        <span className="font-bold tracking-tight text-primary">NIRC SCO</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Portal
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3" aria-label="Portal navigation">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={18}
                      className={cn(
                        'shrink-0',
                        isActive ? 'text-primary-foreground' : 'text-muted-foreground'
                      )}
                    />
                    <span className="truncate">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User + sign out */}
      <div className="border-t border-border p-3">
        {user && (
          <div className="mb-2 px-2">
            <p className="truncate text-sm font-medium text-foreground">{user.full_name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        )}
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
          <LogOut size={18} className="shrink-0" />
          <span>{logout.isPending ? 'Signing out…' : 'Sign out'}</span>
        </button>
      </div>
    </aside>
  );
}
